/**
 * Inputs: owner answers about supplier cost and whether it is already paid.
 * Outputs: field values for applyPlanBooking. Shown when a day plan is booked.
 */

import type { Booking, TripDocument } from "@shared/schema.ts";
import { bookingHasFinancialHistory } from "@shared/financial.ts";
import { formatMinor } from "@shared/money.ts";
import {
  planPaymentPreview,
  type PlanBookingDraft,
  type PlanCostShare,
  type SupplierPaid,
} from "@shared/plan-booking.ts";
import { Link } from "react-router";

export function PlanBookingFields({
  trip,
  draft,
  onChange,
  existing,
}: {
  trip: TripDocument;
  draft: PlanBookingDraft;
  onChange: (next: PlanBookingDraft) => void;
  existing?: Booking;
}) {
  const hasHistory = existing ? bookingHasFinancialHistory(trip, existing.id) : false;
  const preview = planPaymentPreview(trip, draft);
  function set<K extends keyof PlanBookingDraft>(key: K, value: PlanBookingDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  if (hasHistory && existing) {
    return (
      <section className="card">
        <h2 className="serif">Booking</h2>
        <p>This plan is already in Bookings{existing.cost ? ` · ${formatMinor(existing.cost.currency, existing.cost.amountMinor)}` : ""}.</p>
        <p><Link to={`/bookings/${existing.id}`}>Open the booking</Link></p>
      </section>
    );
  }

  return (
    <section className="card">
      <h2 className="serif">Booking and payment</h2>
      <p className="muted">A booked plan needs a Bookings record. Payment is optional until money has actually gone to the supplier.</p>
      <label className="field">
        Supplier or operator
        <input value={draft.provider} onChange={(e) => set("provider", e.target.value)} placeholder="Who we booked with" />
      </label>
      <label className="field">
        Confirmation (optional)
        <input value={draft.confirmation} onChange={(e) => set("confirmation", e.target.value)} />
      </label>
      <label className="field">
        Amount
        <input value={draft.amount} onChange={(e) => set("amount", e.target.value)} inputMode="decimal" placeholder="Leave blank if the price is still unknown" />
      </label>
      <label className="field">
        Currency
        <select value={draft.currency} onChange={(e) => set("currency", e.target.value as "THB" | "GBP")}>
          <option value="THB">THB</option>
          <option value="GBP">GBP</option>
        </select>
      </label>
      <label className="field">
        Who is this cost for?
        <select value={draft.share} onChange={(e) => set("share", e.target.value as PlanCostShare)}>
          <option value="split">Split between Chris and Paul</option>
          <option value="chris">Chris only</option>
          <option value="paul">Paul only</option>
        </select>
      </label>
      <label className="field">
        Has the supplier been paid?
        <select value={draft.paid} onChange={(e) => set("paid", e.target.value as SupplierPaid)}>
          <option value="unpaid">Not paid yet</option>
          <option value="paid">Paid in full</option>
          <option value="unknown">Not sure yet</option>
        </select>
      </label>
      {draft.paid === "paid" ? (
        <>
          <label className="field">
            Who paid the supplier?
            <select value={draft.payerHouseholdId} onChange={(e) => set("payerHouseholdId", e.target.value as "chris" | "paul")}>
              {trip.households.map((h) => (
                <option key={h.id} value={h.id}>{h.label}</option>
              ))}
            </select>
          </label>
          <label className="field">
            Payment date
            <input type="date" value={draft.paidDate} onChange={(e) => set("paidDate", e.target.value)} />
          </label>
          {preview ? <p>{preview}</p> : <p className="muted">Enter the amount to see who would owe whom.</p>}
        </>
      ) : (
        <p className="muted">
          {draft.paid === "unpaid"
            ? "It will show in Bookings as unpaid until a payment is recorded."
            : "Cost can stay unknown. You can add payment later from Bookings."}
        </p>
      )}
    </section>
  );
}
